import { Card, Skeleton } from "@closer/ui";
import type { ReactNode } from "react";

import { StateView } from "../../components/StateView";
import { resolveErrorState } from "../../lib/uiState";

/**
 * Sözleşme detayının bölüm KARTLARI.
 *
 * Beş bölüm sayfası (Özet, Belge, Süreç, Taraflar, Kayıt) ayrı rotalara çıkınca bu iki
 * yardımcı iki dosyada birden gerekti. Kopyalamak yerine buraya alındı; `packages/ui`'ye
 * DEĞİL, çünkü ikisi de sözleşme detayına özgü sözleşmeler taşıyor (`durum` nesnesi
 * TanStack Query'nin şeklinde, boş durum metni bölüme özgü).
 */

/** Boş bölüm BOŞLUK değil cümle gösteriyor: boşluk "yüklenmedi mi?" sorusunu doğurur. */
export function Bolum({
  baslik,
  aciklama,
  durum,
  bos,
  bosMetni = "Henüz kayıt yok.",
  eylem,
  children,
}: {
  baslik: string;
  /**
   * Bölümün NE OLMADIĞINI söyleyen yardımcı metin (Revizyon raporu Faz 1).
   *
   * ⚠ Süs değil: taraf/sorumlu/erişim üç ayrı kavram ve ekranda yan yana duruyorlar.
   * Kullanıcı "taraf eklersem karşı taraf sözleşmeyi görür mü?" sorusunu ancak buradan
   * cevaplayabiliyor; cevabı deneyerek öğrenmesi, yanlış cevabı erişim vererek öğrenmesi
   * demekti.
   *
   * Opsiyonel: her bölümün açıklanacak bir yanlış anlaması yok.
   */
  aciklama?: string;
  durum: { isPending: boolean; error: unknown };
  bos?: boolean;
  /** Başlık hizasındaki eylem; yoksa başlık tek başına kalır. */
  eylem?: ReactNode;
  /** Boş durum metni BÖLÜME ÖZGÜ: "henüz kayıt yok" kullanıcıya neyin eksik olduğunu söylemez. */
  bosMetni?: string;
  children: ReactNode;
}) {
  return (
    <Card>
      <div className="mb-3 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="mr-auto text-base font-semibold text-fg-heading">{baslik}</h2>
          {eylem}
        </div>
        {/*
          ⚠ Açıklama BAŞLIĞIN ALTINDA ve yükleme/hata durumundan ÖNCE duruyor: bölüm
          düşse bile kullanıcı o bölümün ne olduğunu okuyabilmeli. Hata dalının içine
          koysaydık, tam da kafası karıştığı anda kaybolurdu.
        */}
        {aciklama ? <p className="text-sm text-fg-muted">{aciklama}</p> : null}
      </div>
      {durum.isPending ? (
        <Skeleton lines={3} />
      ) : durum.error ? (
        /**
         * ⚠ Düşen bölüm KENDİ güvenli durumunu gösteriyor ve hiçbir veri çizmiyor. Metin
         * `resolveErrorState`'ten; bölüm kendi cümlesini uydurmuyor.
         */
        <StateView
          state={resolveErrorState(durum.error, {
            idempotent: true,
            overrides: {
              error: { title: `${baslik} yüklenemedi`, description: "Bölüm şu an getirilemedi." },
            },
          })}
        />
      ) : bos ? (
        <p className="text-sm text-fg-muted">{bosMetni}</p>
      ) : (
        children
      )}
    </Card>
  );
}

/**
 * Etiket–değer satırı.
 *
 * ⚠ Etiket DEĞERİN ÜSTÜNDE, yanında değil. Yan yana dizildiğinde uzun bir değer (taraf
 * adı, adres) etiketi ekranın soluna itiyor ve iki sütun arasında göz gidip geliyordu;
 * onaylı tasarımda etiket küçük ve gri, değer altında ve koyu. Ayraç çizgisi de kalktı:
 * kart zaten bir yüzey, içindeki her satırı çizgiyle bölmek onu tabloya çeviriyordu.
 */
export function Satir({
  etiket,
  deger,
  eylem,
}: {
  etiket: string;
  deger: ReactNode;
  eylem?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="text-xs text-fg-soft">{etiket}</span>
        <span className="text-sm font-medium text-fg-heading">{deger}</span>
      </span>
      {eylem}
    </div>
  );
}
